import React from 'react';
import { Grid, Stack, Typography } from '@mui/material';
import { Chip } from '@components/chip';
import { getQueryParamForAppliedFilter } from '@components/table/TableCreateFiltersModal/getQueryParamForAppliedFilter';
import { ValueLabelPair } from '@components/table/types/DateMinMax.type';
import { TFilterValues } from '@components/table/types/TableFilterValues.type';

type PinnedFilterChipsProps = {
  pinnedFilterChips: {
    label: string;
    value: string;
    filter: any;
    idToFind?: string;
  }[];
  appliedFilters?: TFilterValues;
  pinnedFilters: TFilterValues;
  handleRemoveFilter: (fieldKey: string, idToFind?: string) => void;
  handleUpdateFilters: (filters: TFilterValues) => void;
  searchParamsInTheUrl: URLSearchParams;
  setSearchParamsInTheUrl: (params: URLSearchParams, options?: { replace?: boolean }) => void;
};

export const PinnedFilterChips: React.FC<PinnedFilterChipsProps> = ({
  pinnedFilterChips,
  appliedFilters,
  pinnedFilters,
  handleRemoveFilter,
  handleUpdateFilters,
  searchParamsInTheUrl,
  setSearchParamsInTheUrl,
}) => {
  const getFilterValue = (
    value: string | ValueLabelPair | ValueLabelPair[] | string[],
    valueToFind?: string | ValueLabelPair,
  ) => {
    if (Array.isArray(value)) {
      const idToFind = typeof valueToFind === 'string' ? valueToFind : valueToFind?.value;
      const item = value?.find((x: any) => x === idToFind || x?.value === idToFind);

      if (typeof item === 'string') {
        return item;
      }
      return item?.value;
    } else {
      if (typeof value === 'string') {
        return value;
      }
      return value?.value;
    }
  };

  return (
    <Grid size={{ xs: 12 }}>
      <Stack
        direction='row'
        sx={{
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '8px',
        }}
      >
        <Typography
          variant='caption'
          sx={{
            color: 'primary.dark',
            marginRight: '8px',
            fontWeight: 'bold',
          }}
        >
          Pinned Filters
        </Typography>
        {pinnedFilterChips.map(({ label, value, filter, idToFind }) => {
          let enabled = false;

          const appliedValue = appliedFilters?.[filter.field];

          if (appliedValue !== undefined) {
            const isMulti = filter?.fieldConfig?.selectField?.multiSelect;
            if (!isMulti) {
              enabled =
                getFilterValue(appliedFilters?.[filter.field], idToFind) ===
                getFilterValue(pinnedFilters[filter.field], idToFind);
            } else {
              const appliedValuesArray = appliedFilters?.[filter.field]?.map((t: ValueLabelPair | string) =>
                typeof t === 'string' ? t : t.value,
              );
              enabled = appliedValuesArray.includes(idToFind);
            }
          }

          return (
            <Chip
              key={`${label}: ${value}`}
              label={`${label}: ${value}`}
              onClick={() => {
                if (enabled) {
                  handleRemoveFilter(filter.field, idToFind);
                } else {
                  const pinnedFilterValue = pinnedFilters[filter.field];

                  if (idToFind !== undefined) {
                    // handle turn on single
                    const itemToEnable = pinnedFilterValue.find((x: any) => x === idToFind || x?.value === idToFind);
                    const newFilterValues = [...(appliedFilters?.[filter.field] || []), itemToEnable];

                    const queryParam = getQueryParamForAppliedFilter(filter.field, newFilterValues);

                    if (queryParam !== null) {
                      searchParamsInTheUrl.set(filter.field, queryParam);
                      setSearchParamsInTheUrl(searchParamsInTheUrl, { replace: true });
                    }

                    handleUpdateFilters({
                      [filter.field]: newFilterValues,
                    });
                  } else {
                    const queryParam = getQueryParamForAppliedFilter(filter.field, pinnedFilterValue);
                    if (queryParam !== null) {
                      searchParamsInTheUrl.set(filter.field, queryParam);
                      setSearchParamsInTheUrl(searchParamsInTheUrl, { replace: true });
                    }

                    handleUpdateFilters({
                      [filter.field]: pinnedFilterValue,
                    });
                  }
                }
              }}
              variant={enabled ? 'filled' : 'outlined'}
              sxProps={
                enabled
                  ? {
                      backgroundColor: 'gray !important',
                      color: 'white',
                    }
                  : {
                      color: 'gray',
                    }
              }
            />
          );
        })}
      </Stack>
    </Grid>
  );
};
